import type { CSSProperties } from "react";

export type StickerProps = {
    text: string;

    lines?: string[];
    fontFamily?: string;
    fontWeight?: number | string;

    color?: string;

    outlineColor?: string;

    outlineWidth?: number;

    fontSize?: number | string;
    lineHeight?: number;

    shadow?: boolean | string;

    rotation?: number;
    className?: string;
};

const DEFAULTS = {
    fontFamily: "var(--font-family-heading), serif",
    fontWeight: 700,
    color: "var(--color-doberman-black, #1f1712)",
    outlineColor: "var(--color-true-white, #ffffff)",
    outlineWidth: 7,
    fontSize: 34,
    lineHeight: 1.02,
    shadow: true,
    rotation: -3,
};

export function Sticker({
    text,
    lines,
    className,
    fontFamily = DEFAULTS.fontFamily,
    fontWeight = DEFAULTS.fontWeight,
    color = DEFAULTS.color,
    outlineColor = DEFAULTS.outlineColor,
    outlineWidth = DEFAULTS.outlineWidth,
    fontSize = DEFAULTS.fontSize,
    lineHeight = DEFAULTS.lineHeight,
    shadow = DEFAULTS.shadow,
    rotation = DEFAULTS.rotation,
}: StickerProps) {
    const rows = lines?.length ? lines : text.split("\n");

    const sizePx = typeof fontSize === "number" ? fontSize : 30;
    const halo = `drop-shadow(0 0 ${(sizePx * 0.055).toFixed(2)}px rgba(0,0,0,0.34))`;
    const lift = `drop-shadow(0 ${(sizePx * 0.067).toFixed(2)}px ${(sizePx * 0.13).toFixed(2)}px rgba(0,0,0,0.04))`;
    const autoShadow = `${halo} ${lift}`;
    const filter =
        shadow === false
            ? undefined
            : typeof shadow === "string"
              ? shadow
              : autoShadow;

    const inkPad = filter ? Math.ceil(outlineWidth + sizePx * 0.22) : 0;

    const textStyle: CSSProperties = {
        fontFamily,
        fontWeight,
        fontSize,
        lineHeight,
        color,
        letterSpacing: "-0.01em",
        WebkitTextStrokeColor: outlineColor,
        WebkitTextStrokeWidth: `${outlineWidth * 2}px`,
        paintOrder: "stroke fill",
        filter,
        ...(inkPad
            ? {
                  display: "inline-block",
                  padding: `${inkPad}px`,
                  margin: `${-inkPad}px`,
              }
            : null),
    };
    const rowOccurrences = new Map<string, number>();
    const keyedRows = rows.map((row) => {
        const occurrence = rowOccurrences.get(row) ?? 0;
        rowOccurrences.set(row, occurrence + 1);
        return { key: `${row}-${occurrence}`, row };
    });

    return (
        <span
            className={className}
            style={{
                display: "inline-block",
                transform: rotation ? `rotate(${rotation}deg)` : undefined,
            }}
        >
            <span style={textStyle}>
                {keyedRows.map(({ key, row }) => (
                    <span key={key} style={{ display: "block" }}>
                        {row}
                    </span>
                ))}
            </span>
        </span>
    );
}
